import { post, destroy, put } from 'api';
import { CancelToken, CancelTokenSource } from 'axios';
import { AUTOMATION_API_ENDPOINTS } from './automation-constants';
import { BaseResponse } from 'common/utils/shared/shared-models';

export interface AutomationSettingListingRef {
    reload: () => void;
    isExistingStatus: (statusId: string, dealerId: number) => boolean;
}

export interface DepartmentPhoneResponse {
    departmentId: number;
    departmentName: string;
    phoneNumber: string;
}
export interface GetWorkOrderStatusesRequest {
    dealerId: number;
    dmsType: number;    
    isStatusCode: boolean;
}
export interface AutomationGetDepartmentsRequest {
    dealerId: number;
}

export interface DepartmentResponse {
    dealerLocationId: number;
    Departments: DepartmentPhoneResponse[];
};

export interface GetAutomationSettingRequest {
    dmsDealerId: number;
}

export interface UpdateAutomationStatusRequest {
    Id: number;
    IsActive: boolean;
}

export interface AutomationSetting {
    id: number;
    dealerId: number;
    departmentId: number;
    locationName: string;
    departmentName: string
    orderStatus: string;
    orderStatusId: string;
    startingHour: number;
    endingHour: number;
    delayMessage?: number;

    isText: boolean;
    textMessage?: string;

    isEmail: boolean;
    emailSubject?: string;
    emailMessage?: string;
    emailPreHeader?: string;

    isTask: boolean;
    taskSubject?: string;
    taskTypeId?: number;
    taskDueDays: number;
    taskNotes?: string;
    assignedUserId?: string;

    isStatusCode: boolean;
    isSurvey: boolean;
    isActive?: boolean;
    createdBy?: string;
    creationDate: Date;
    updatedBy?: string;
    updationDate?: Date;

}

export interface AddUpdateAutomationSettingRequest {
    id: number;
    dealerId: number;
    departmentId: number;
    orderStatus: string;
    orderStatusId: string;
    startingHour: number;
    endingHour: number;
    isActive: boolean;
    delayMessage?: number;

    isText: boolean;
    textMessage?: string;

    isEmail: boolean;
    emailSubject?: string;
    emailMessage?: string;
    emailPreHeader?: string;

    isTask: boolean;
    taskSubject?: string;
    taskTypeId?: number;
    TaskDueDays: number;
    TaskNotes: string;
    AssignedUserId?: string;

    isSurvey: boolean;
    isStatusCode: boolean;    

    createdBy?: string;
    updatedBy?: string;

}

export interface GetTaskLogResponse {
    Id: number
    dateCreated: Date;
    invoiceId: number;
    jobId: number;
    orderStatus: string;
    assignedUser: string;
    taskSubject: string;
}
export interface AutomationTaskLogRequest {
    dmsDealerId: number;
}
export interface AutomationTaskReadyToSendRequest {
    dmsDealerId: number;
}
export interface AutomationEmailReadyToSendRequest {
    dmsDealerId: number;
}

export interface AutomationEmailLogRequest {
    dmsDealerId: number;
}
export class AutomationEmailLogResponse {
    dateCreated!: Date;
    emailAddress!: string;
    emailSubject!: string;
    invoiceID?: number;
    jobId?: number;
    orderStatus!: string;
}


export class AutomationEmailReadyToSendResponse {
    automationId!: number;
    dealerId!: number;
    invoiceId!: number;
    customerId!: number;
    jobStatus?: string;
    jobDescription?: string;
    jobId!: number;
    dealerName: string = '';
    dealerTime!: Date;
    emailAddress: string = '';
    emailPreHeader?: string;
    emailSubject?: string;
    emailMessage?: string;
    jobCount!: number;
}

export interface AutomationTextLogRequest {
    dmsDealerId: number;
}

export class AutomationTextLogResponse {
    dateCreated!: Date;
    departmentName!: string;
    from!: string;
    to!: string;
    status!: string;
    body!: string;
    invoiceId!: number;
    jobId!: number;
    orderStatus!: string;
}

export interface AutomationTextReadyToSendRequest {
    dmsDealerId: number;
}
export class AutomationTextReadyToSendResponse {
    automationId!: number;
    dealerId!: number;
    invoiceId!: number;
    customerId!: number;
    jobStatus!: string;
    jobDescription!: string;
    jobId!: number;
    phoneNumberCustomer!: string;
    phoneNumberDepartment!: string;
    textMessage!: string;
    jobCount!: number;
}
export interface GetReadyToSendTaskDataResponse {
    automationId: number;
    dealerId: number;
    invoiceId: number;
    customerId: number;
    jobStatus: string;
    jobDescription: string;
    jobId: number;
    jobCount: number;
    departmentId: number;
    assignedUserId: string;
    taskTypeId?: number;
    taskDueDays: number;
    createdBy: string;
    taskSubject: string;
    taskNotes: string;
    assignedUser: string;
}

export interface GetWorkOrderStatusesResponse {
    jobStatusId: string;
    jobStatus: string;
}

export interface AutomationFilter {
    locationDealerIds: number[];
    departmentIds: number[];
    workOrderStatusIds: string[];
    automationTypeIds: number[]
    statusIds: number[]
};

export interface AutomationGetDepartmentsRequest {
    dealerId: number;
}

const AutomationAPI = {

    GetAutomationSetting: async (
        getAutomationSettingRequest: GetAutomationSettingRequest,
        cancelToken: CancelTokenSource | null
    ): Promise<BaseResponse<Array<AutomationSetting>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetAutomationSettings;
        const { data } = await post<BaseResponse<Array<AutomationSetting>>>(
            url,
            getAutomationSettingRequest,
            {
                cancelToken: cancelToken?.token,
            });
        return data;
    },

    AddUpdateAutomationSetting: async (
        getAutomationSettingRequest: AddUpdateAutomationSettingRequest
    ): Promise<BaseResponse<boolean>> => {

        const url = AUTOMATION_API_ENDPOINTS.AddUpdateAutomationSetting;
        const { data } = await post<BaseResponse<boolean>>(url, getAutomationSettingRequest);
        return data;
    },
    UpdateAutomationStatus: async (
        updateAutomationStatusRequest: UpdateAutomationStatusRequest
    ): Promise<BaseResponse<boolean>> => {

        const url = AUTOMATION_API_ENDPOINTS.UpdateAutomationStatus;
        const { data } = await put<BaseResponse<boolean>>(url, updateAutomationStatusRequest);
        return data;
    },

    DeleteAutomationSetting: async (
        automationId: number
    ): Promise<BaseResponse<boolean>> => {

        const url = AUTOMATION_API_ENDPOINTS.DeleteAutomationSetting(automationId);
        const { data } = await destroy<BaseResponse<boolean>>(url);
        return data;
    },
    GetTaskLogs: async (
        request: AutomationTaskLogRequest,
        cancelToken?: CancelToken
    ): Promise<BaseResponse<Array<GetTaskLogResponse>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetTaskLogs;
        const { data } = await post<BaseResponse<Array<GetTaskLogResponse>>>(url, request,{cancelToken});
        return data;
    },
    GetReadyToSendTaskData: async (
        request: AutomationTaskReadyToSendRequest,
        cancelToken?: CancelToken
    ): Promise<BaseResponse<Array<GetReadyToSendTaskDataResponse>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetReadyToSendTaskData;
        const { data } = await post<BaseResponse<Array<GetReadyToSendTaskDataResponse>>>(url, request,{cancelToken});
        return data;
    },
    GetEmailLogs: async (
        request: AutomationEmailLogRequest,
        cancelToken?: CancelToken
    ): Promise<BaseResponse<Array<AutomationEmailLogResponse>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetEmailLogs;
        const { data } = await post<BaseResponse<Array<AutomationEmailLogResponse>>>(url, request,{cancelToken});
        return data;
    },
    GetReadyToSendEmailData: async (
        request: AutomationEmailReadyToSendRequest,
        cancelToken?: CancelToken
    ): Promise<BaseResponse<Array<AutomationEmailReadyToSendResponse>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetReadyToSendEmailData;
        const { data } = await post<BaseResponse<Array<AutomationEmailReadyToSendResponse>>>(url, request,{cancelToken});
        return data;
    },
    GetTextLogs: async (
        request: AutomationTextLogRequest,
        cancelToken?: CancelToken
    ): Promise<BaseResponse<Array<AutomationTextLogResponse>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetTextLogs;
        const { data } = await post<BaseResponse<Array<AutomationTextLogResponse>>>(url, request,{cancelToken});
        return data;
    },
    GetReadyToSendTextData: async (
        request: AutomationTextReadyToSendRequest,
        cancelToken?: CancelToken
    ): Promise<BaseResponse<Array<AutomationTextReadyToSendResponse>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetReadyToSendTextData;
        const { data } = await post<BaseResponse<Array<AutomationTextReadyToSendResponse>>>(url, request,{cancelToken});
        return data;
    },
    GetWorkOrderStatuses: async (
        request: GetWorkOrderStatusesRequest,
        cancelToken?: CancelToken
    ): Promise<BaseResponse<Array<GetWorkOrderStatusesResponse>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetWorkOrderStatuses;
        const { data } = await post<BaseResponse<Array<GetWorkOrderStatusesResponse>>>(url, request,{cancelToken});
        return data;
    },
    GetAutomationDepartments: async (
        request: AutomationGetDepartmentsRequest
    ): Promise<BaseResponse<Array<DepartmentPhoneResponse>>> => {

        const url = AUTOMATION_API_ENDPOINTS.GetAutomationDepartments;
        const { data } = await post<BaseResponse<Array<DepartmentPhoneResponse>>>(url, request);
        return data;
    },

};

export default AutomationAPI;
